import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Warehouse, LayoutGrid, MapPin, Workflow, Plus, Pencil, Power } from "lucide-react";
import { get, post, put as apiPut } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { LoadingState, EmptyState } from "@/components/common/States";
import { TableauDetail } from "@/components/analyse/TableauDetail";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const ONGLETS = [
  { key: "entrepots", label: "Entrepôts", icon: Warehouse },
  { key: "zones", label: "Zones / Sections", icon: LayoutGrid },
  { key: "emplacements", label: "Emplacements", icon: MapPin },
  { key: "mapping", label: "Mapping lignes", icon: Workflow },
];

const TYPES_LABELS = {
  matieres_premieres: "Matières premières", produits_finis: "Produits finis", pieces: "Pièces",
  expedition: "Expédition", quarantaine: "Quarantaine", autre: "Autre",
};

const Field = ({ label, children, required }) => (
  <label className="block text-[11px] font-mono text-muted-foreground mb-2">
    {label}{required && <span className="text-destructive"> *</span>}
    <div className="mt-1">{children}</div>
  </label>
);

const inputCls = "w-full h-8 bg-input border border-border rounded-sm px-2 text-xs text-foreground";

// ============================ ENTREPÔT dialog ============================
const EntrepotDialog = ({ open, onOpenChange, entrepot, users, onSaved }) => {
  const [f, setF] = useState(() => entrepot || { code: "", nom: "", description: "", adresse: "",
    responsable_user_id: "", type: "autre", par_defaut: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, responsable_user_id: f.responsable_user_id ? Number(f.responsable_user_id) : null };
      return entrepot ? apiPut(`/inventaire/entrepots/${entrepot.id}`, body) : post("/inventaire/entrepots", body);
    },
    onSuccess: () => { toast.success(entrepot ? "Entrepôt modifié" : "Entrepôt créé"); onSaved(); onOpenChange(false); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="dialog-entrepot">
        <DialogHeader><DialogTitle>{entrepot ? "Modifier l'entrepôt" : "Nouvel entrepôt"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" required><input data-testid="entrepot-code" className={inputCls} value={f.code} onChange={set("code")} /></Field>
          <Field label="Nom" required><input data-testid="entrepot-nom" className={inputCls} value={f.nom} onChange={set("nom")} /></Field>
          <Field label="Type"><select data-testid="entrepot-type" className={inputCls} value={f.type} onChange={set("type")}>
            {Object.entries(TYPES_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></Field>
          <Field label="Responsable"><select data-testid="entrepot-responsable" className={inputCls} value={f.responsable_user_id || ""} onChange={set("responsable_user_id")}>
            <option value="">—</option>
            {(users || []).filter((u) => u.actif).map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
          </select></Field>
          <Field label="Adresse"><input data-testid="entrepot-adresse" className={inputCls} value={f.adresse} onChange={set("adresse")} /></Field>
          <Field label="Description"><input data-testid="entrepot-desc" className={inputCls} value={f.description} onChange={set("description")} /></Field>
          <label className="flex items-center gap-2 text-xs col-span-2"><input data-testid="entrepot-defaut" type="checkbox" checked={!!f.par_defaut} onChange={set("par_defaut")} />Entrepôt par défaut (réception de la production)</label>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button data-testid="entrepot-cancel" className="h-8 px-3 text-xs border border-border rounded-sm" onClick={() => onOpenChange(false)}>Annuler</button>
          <button data-testid="entrepot-save" disabled={!f.code || !f.nom || save.isPending} className="h-8 px-3 text-xs bg-primary text-primary-foreground rounded-sm disabled:opacity-50" onClick={() => save.mutate()}>{entrepot ? "Enregistrer" : "Créer"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================ ZONE dialog ============================
const ZoneDialog = ({ open, onOpenChange, zone, entrepots, defaultEntrepot, onSaved }) => {
  const [f, setF] = useState(() => zone || { code: "", nom: "", description: "", entrepot_id: defaultEntrepot || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, entrepot_id: Number(f.entrepot_id) };
      return zone ? apiPut(`/inventaire/zones/${zone.id}`, body) : post("/inventaire/zones", body);
    },
    onSuccess: () => { toast.success(zone ? "Zone modifiée" : "Zone créée"); onSaved(); onOpenChange(false); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-zone">
        <DialogHeader><DialogTitle>{zone ? "Modifier la zone" : "Nouvelle zone"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Entrepôt" required><select data-testid="zone-entrepot" className={inputCls} value={f.entrepot_id} disabled={!!zone} onChange={set("entrepot_id")}>
            <option value="">—</option>
            {(entrepots || []).filter((e) => e.actif).map((e) => <option key={e.id} value={e.id}>{e.code} — {e.nom}</option>)}
          </select></Field>
          <Field label="Code" required><input data-testid="zone-code" className={inputCls} value={f.code} onChange={set("code")} /></Field>
          <Field label="Nom" required><input data-testid="zone-nom" className={inputCls} value={f.nom} onChange={set("nom")} /></Field>
          <Field label="Description"><input data-testid="zone-desc" className={inputCls} value={f.description} onChange={set("description")} /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button className="h-8 px-3 text-xs border border-border rounded-sm" onClick={() => onOpenChange(false)}>Annuler</button>
          <button data-testid="zone-save" disabled={!f.code || !f.nom || !f.entrepot_id || save.isPending} className="h-8 px-3 text-xs bg-primary text-primary-foreground rounded-sm disabled:opacity-50" onClick={() => save.mutate()}>{zone ? "Enregistrer" : "Créer"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================ EMPLACEMENT dialog ============================
const EmplacementDialog = ({ open, onOpenChange, empl, zones, entrepots, onSaved }) => {
  const [f, setF] = useState(() => empl || { code: "", nom: "", type: "entrepot", par_defaut: false, zone_id: "" });
  const zone = (zones || []).find((z) => String(z.id) === String(f.zone_id));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, zone_id: f.zone_id ? Number(f.zone_id) : null };
      return empl ? apiPut(`/inventaire/emplacements/${empl.id}`, body) : post("/inventaire/emplacements", body);
    },
    onSuccess: () => { toast.success(empl ? "Emplacement modifié" : "Emplacement créé"); onSaved(); onOpenChange(false); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="dialog-emplacement">
        <DialogHeader><DialogTitle>{empl ? "Modifier l'emplacement" : "Nouvel emplacement"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Zone (hérite de l'entrepôt)"><select data-testid="empl-zone" className={inputCls} value={f.zone_id || ""} onChange={set("zone_id")}>
            <option value="">—</option>
            {(zones || []).filter((z) => z.actif).map((z) => <option key={z.id} value={z.id}>{z.entrepot} / {z.nom}</option>)}
          </select></Field>
          <Field label="Code" required><input data-testid="empl-code" className={inputCls} value={f.code} onChange={set("code")} /></Field>
          <Field label="Nom" required><input data-testid="empl-nom" className={inputCls} value={f.nom} onChange={set("nom")} /></Field>
          <Field label="Type (hérité)"><input className={inputCls} value={f.type} onChange={set("type")} /></Field>
          <label className="flex items-center gap-2 text-xs col-span-2"><input data-testid="empl-defaut" type="checkbox" checked={!!f.par_defaut} onChange={set("par_defaut")} />Emplacement par défaut (réception production si aucun mapping)</label>
          {zone && <div className="col-span-2 text-[11px] font-mono text-muted-foreground">Entrepôt rattaché : <b>{zone.entrepot}</b></div>}
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <button className="h-8 px-3 text-xs border border-border rounded-sm" onClick={() => onOpenChange(false)}>Annuler</button>
          <button data-testid="empl-save" disabled={!f.code || !f.nom || save.isPending} className="h-8 px-3 text-xs bg-primary text-primary-foreground rounded-sm disabled:opacity-50" onClick={() => save.mutate()}>{empl ? "Enregistrer" : "Créer"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================ PAGE ============================
export default function EntrepotsPage() {
  const qc = useQueryClient();
  const [onglet, setOnglet] = useState("entrepots");
  const [filtreEntrepot, setFiltreEntrepot] = useState("");
  const [dialogEntrepot, setDialogEntrepot] = useState(null); // null | "new" | entrepot
  const [dialogZone, setDialogZone] = useState(null);
  const [dialogEmpl, setDialogEmpl] = useState(null);

  const feats = useQuery({ queryKey: ["features"], queryFn: () => get("/me/features") });
  const actif = feats.data?.modules?.inventory?.actif;
  const perms = feats.data?.permissions || [];
  const peutWarehouses = perms.includes("inventory.warehouses");

  const entrepots = useQuery({ queryKey: ["entrepots"], queryFn: () => get("/inventaire/entrepots"), enabled: !!actif });
  const zones = useQuery({ queryKey: ["zones", filtreEntrepot], queryFn: () => get("/inventaire/zones", filtreEntrepot ? { entrepot_id: filtreEntrepot } : {}), enabled: !!actif });
  const empls = useQuery({ queryKey: ["empls-all"], queryFn: () => get("/inventaire/emplacements", { actifs_seulement: false }), enabled: !!actif });
  const users = useQuery({ queryKey: ["users-admin"], queryFn: () => get("/users"), enabled: !!actif, retry: false });
  const mapping = useQuery({ queryKey: ["ligne-mapping"], queryFn: () => get("/inventaire/lignes/emplacement-defaut"), enabled: !!actif && onglet === "mapping" });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["entrepots"] }); qc.invalidateQueries({ queryKey: ["zones"] }); qc.invalidateQueries({ queryKey: ["empls-all"] }); qc.invalidateQueries({ queryKey: ["ligne-mapping"] }); };

  const toggleEntrepot = useMutation({
    mutationFn: (id) => post(`/inventaire/entrepots/${id}/toggle`, {}),
    onSuccess: (d) => { toast.success(d.actif ? "Entrepôt réactivé" : "Entrepôt désactivé"); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const toggleZone = useMutation({
    mutationFn: (id) => post(`/inventaire/zones/${id}/toggle`, {}),
    onSuccess: (d) => { toast.success(d.actif ? "Zone réactivée" : "Zone désactivée"); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const toggleEmpl = useMutation({
    mutationFn: (id) => post(`/inventaire/emplacements/${id}/toggle`, {}),
    onSuccess: (d) => { toast.success(d.actif ? "Emplacement réactivé" : "Emplacement désactivé"); invalidate(); },
    onError: (e) => toast.error(e.message),
  });
  const setMapping = useMutation({
    mutationFn: ({ ligne_id, emplacement_id }) => apiPut("/inventaire/lignes/emplacement-defaut", { ligne_id, emplacement_id }),
    onSuccess: () => { toast.success("Mapping enregistré"); invalidate(); },
    onError: (e) => toast.error(e.message),
  });

  if (feats.isLoading) return <LoadingState rows={4} />;
  if (!actif) return (
    <div className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Inventaire" }, { label: "Configuration" }]} title="Configuration Inventaire" />
      <EmptyState title="Module non souscrit" hint="Activez le module « Inventaire & Traçabilité » depuis le Super Admin." />
    </div>
  );
  if (!peutWarehouses) return (
    <div className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Inventaire" }, { label: "Configuration" }]} title="Configuration Inventaire" />
      <EmptyState title="Permission requise" hint="Seul un administrateur peut gérer l'arborescence Entrepôt → Zone → Emplacement (`inventory.warehouses`)." />
    </div>
  );

  const colonnesEntrepots = [
    { cle: "code", label: "Code", mono: true },
    { cle: "nom", label: "Nom" },
    { cle: "type", label: "Type", render: (r) => TYPES_LABELS[r.type] || r.type },
    { cle: "responsable", label: "Responsable", render: (r) => r.responsable || "—" },
    { cle: "nb_zones", label: "Zones", align: "right" },
    { cle: "nb_emplacements", label: "Empl.", align: "right" },
    { cle: "par_defaut", label: "Défaut", render: (r) => r.par_defaut ? <span className="text-emerald-400">★</span> : "" },
    { cle: "actif", label: "Statut", render: (r) => r.actif ? <span className="text-emerald-400">● actif</span> : <span className="text-muted-foreground">○ inactif</span> },
    { cle: "actions", label: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        <button data-testid={`entrepot-edit-${r.id}`} className="p-1 hover:bg-muted rounded-sm" onClick={() => setDialogEntrepot(r)}><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`entrepot-toggle-${r.id}`} className="p-1 hover:bg-muted rounded-sm" onClick={() => toggleEntrepot.mutate(r.id)}><Power className={cn("h-3.5 w-3.5", r.actif ? "text-emerald-400" : "text-muted-foreground")} /></button>
      </div>
    ) },
  ];
  const colonnesZones = [
    { cle: "code", label: "Code", mono: true },
    { cle: "nom", label: "Nom" },
    { cle: "entrepot", label: "Entrepôt" },
    { cle: "nb_emplacements", label: "Empl.", align: "right" },
    { cle: "actif", label: "Statut", render: (r) => r.actif ? <span className="text-emerald-400">● actif</span> : <span className="text-muted-foreground">○ inactif</span> },
    { cle: "actions", label: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        <button data-testid={`zone-edit-${r.id}`} className="p-1 hover:bg-muted rounded-sm" onClick={() => setDialogZone(r)}><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`zone-toggle-${r.id}`} className="p-1 hover:bg-muted rounded-sm" onClick={() => toggleZone.mutate(r.id)}><Power className={cn("h-3.5 w-3.5", r.actif ? "text-emerald-400" : "text-muted-foreground")} /></button>
      </div>
    ) },
  ];
  const colonnesEmpls = [
    { cle: "code", label: "Code", mono: true },
    { cle: "nom", label: "Nom" },
    { cle: "entrepot", label: "Entrepôt", render: (r) => r.entrepot || <span className="text-amber-400">⚠ non rattaché</span> },
    { cle: "zone", label: "Zone", render: (r) => r.zone || "—" },
    { cle: "type", label: "Type" },
    { cle: "par_defaut", label: "Défaut", render: (r) => r.par_defaut ? <span className="text-emerald-400">★</span> : "" },
    { cle: "actif", label: "Statut", render: (r) => r.actif ? <span className="text-emerald-400">● actif</span> : <span className="text-muted-foreground">○ inactif</span> },
    { cle: "actions", label: "", render: (r) => (
      <div className="flex gap-1 justify-end">
        <button data-testid={`empl-edit-${r.id}`} className="p-1 hover:bg-muted rounded-sm" onClick={() => setDialogEmpl(r)}><Pencil className="h-3.5 w-3.5" /></button>
        <button data-testid={`empl-toggle-${r.id}`} className="p-1 hover:bg-muted rounded-sm" onClick={() => toggleEmpl.mutate(r.id)}><Power className={cn("h-3.5 w-3.5", r.actif ? "text-emerald-400" : "text-muted-foreground")} /></button>
      </div>
    ) },
  ];

  return (
    <div data-testid="entrepots-page" className="space-y-3">
      <PageHeader crumbs={[{ label: "ProdTrack" }, { label: "Inventaire" }, { label: "Configuration" }]}
        title="Configuration Inventaire" subtitle="Entrepôts → Zones → Emplacements · Mapping ligne → destination par défaut" />

      <div data-testid="config-onglets" className="panel px-1.5 py-1.5 flex flex-wrap gap-1 overflow-x-auto">
        {ONGLETS.map((o) => (
          <button key={o.key} data-testid={`config-onglet-${o.key}`} onClick={() => setOnglet(o.key)}
            className={cn("h-7 px-3 text-[11px] font-semibold rounded-sm flex items-center gap-1.5 whitespace-nowrap",
              onglet === o.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
            <o.icon className="h-3.5 w-3.5" />{o.label}
          </button>
        ))}
      </div>

      {onglet === "entrepots" && (
        <TableauDetail testId="table-entrepots" titre={`Entrepôts (${(entrepots.data || []).length})`}
          data={{ items: entrepots.data || [], total: (entrepots.data || []).length, pages: 1 }}
          page={1} setPage={() => {}} recherche="" setRecherche={() => {}}
          actions={<button data-testid="btn-new-entrepot" onClick={() => setDialogEntrepot("new")} className="h-7 px-3 text-[11px] bg-primary text-primary-foreground rounded-sm flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Nouvel entrepôt</button>}
          colonnes={colonnesEntrepots} />
      )}

      {onglet === "zones" && (
        <TableauDetail testId="table-zones" titre={`Zones · ${filtreEntrepot ? (entrepots.data || []).find((e) => String(e.id) === filtreEntrepot)?.nom : "tous entrepôts"}`}
          data={{ items: zones.data || [], total: (zones.data || []).length, pages: 1 }}
          page={1} setPage={() => {}} recherche="" setRecherche={() => {}}
          actions={<div className="flex gap-1">
            <select data-testid="zones-filtre-entrepot" value={filtreEntrepot} onChange={(e) => setFiltreEntrepot(e.target.value)} className="h-7 bg-input border border-border rounded-sm px-2 text-[11px]">
              <option value="">Tous les entrepôts</option>
              {(entrepots.data || []).map((e) => <option key={e.id} value={e.id}>{e.code} — {e.nom}</option>)}
            </select>
            <button data-testid="btn-new-zone" onClick={() => setDialogZone("new")} className="h-7 px-3 text-[11px] bg-primary text-primary-foreground rounded-sm flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Nouvelle zone</button>
          </div>}
          colonnes={colonnesZones} />
      )}

      {onglet === "emplacements" && (
        <TableauDetail testId="table-emplacements" titre={`Emplacements (${(empls.data || []).length})`}
          data={{ items: empls.data || [], total: (empls.data || []).length, pages: 1 }}
          page={1} setPage={() => {}} recherche="" setRecherche={() => {}}
          actions={<button data-testid="btn-new-empl" onClick={() => setDialogEmpl("new")} className="h-7 px-3 text-[11px] bg-primary text-primary-foreground rounded-sm flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Nouvel emplacement</button>}
          colonnes={colonnesEmpls} />
      )}

      {onglet === "mapping" && (
        <Panel title="Mapping ligne → emplacement de destination par défaut" testId="panel-mapping" bodyClassName="p-3">
          <p className="text-[11px] text-muted-foreground mb-3">La production bonne d'un OF sera automatiquement rangée dans l'emplacement choisi pour la ligne. Laisser vide = fallback sur l'emplacement par défaut de l'entreprise.</p>
          <div className="space-y-2">
            {(mapping.data || []).map((m) => (
              <div key={m.ligne_id} data-testid={`mapping-ligne-${m.ligne_id}`} className="flex items-center gap-2 p-2 border border-border rounded-sm">
                <span className="flex-1 text-xs font-mono">{m.ligne}</span>
                <span className="text-muted-foreground">→</span>
                <select data-testid={`mapping-select-${m.ligne_id}`} value={m.emplacement_id || ""}
                  onChange={(e) => setMapping.mutate({ ligne_id: m.ligne_id, emplacement_id: e.target.value ? Number(e.target.value) : null })}
                  className="h-7 bg-input border border-border rounded-sm px-2 text-[11px] min-w-[220px]">
                  <option value="">— aucun (fallback défaut) —</option>
                  {(empls.data || []).filter((e) => e.actif).map((e) => <option key={e.id} value={e.id}>{e.entrepot ? `${e.entrepot} / ` : ""}{e.nom}</option>)}
                </select>
              </div>
            ))}
            {(mapping.data || []).length === 0 && !mapping.isLoading && <EmptyState title="Aucune ligne" hint="Créez des lignes de production d'abord (Référentiels → Lignes)." />}
          </div>
        </Panel>
      )}

      {dialogEntrepot && <EntrepotDialog open={!!dialogEntrepot} onOpenChange={(v) => !v && setDialogEntrepot(null)}
        entrepot={dialogEntrepot === "new" ? null : dialogEntrepot} users={users.data} onSaved={invalidate} />}
      {dialogZone && <ZoneDialog open={!!dialogZone} onOpenChange={(v) => !v && setDialogZone(null)}
        zone={dialogZone === "new" ? null : dialogZone} entrepots={entrepots.data} defaultEntrepot={filtreEntrepot} onSaved={invalidate} />}
      {dialogEmpl && <EmplacementDialog open={!!dialogEmpl} onOpenChange={(v) => !v && setDialogEmpl(null)}
        empl={dialogEmpl === "new" ? null : dialogEmpl} zones={zones.data} entrepots={entrepots.data} onSaved={invalidate} />}
    </div>
  );
}
